import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import type { User } from '../types/auth';
export type { User };

export interface UserFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  tenantId?: string;
}

export interface UseUsersResult {
  users: User[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  filters: UserFilters;
  setFilters: (filters: UserFilters) => void;
  refresh: () => Promise<void>;
  createUser: (userData: {
    email: string;
    name: string;
    password?: string;
    tenantId: string;
    roleId: string;
    sendInvite?: boolean;
  }) => Promise<boolean>;
  updateUser: (userId: string, updates: {
    name?: string;
    email?: string;
    status?: string;
  }) => Promise<boolean>;
  deleteUser: (userId: string) => Promise<boolean>;
  updateUserRole: (userId: string, tenantId: string, roleId: string) => Promise<boolean>;
  terminateUserSessions: (userId: string) => Promise<boolean>;
}

export function useUsers(initialFilters: UserFilters = {}): UseUsersResult {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    pageSize: 20,
    ...initialFilters,
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getUsers(filters);

      if (response.success && response.data) {
        setUsers(response.data.users);
        setPagination(response.data.pagination);
      } else {
        setError(response.error || 'Failed to fetch users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refresh = useCallback(() => fetchUsers(), [fetchUsers]);

  const createUser = useCallback(async (userData: {
    email: string;
    name: string;
    password?: string;
    tenantId: string;
    roleId: string;
    sendInvite?: boolean;
  }): Promise<boolean> => {
    try {
      const response = await apiClient.createUser(userData);
      if (response.success) {
        await fetchUsers(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to create user');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
      return false;
    }
  }, [fetchUsers]);

  const updateUser = useCallback(async (userId: string, updates: {
    name?: string;
    email?: string;
    status?: string;
  }): Promise<boolean> => {
    try {
      const response = await apiClient.updateUser(userId, updates);
      if (response.success) {
        await fetchUsers(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to update user');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
      return false;
    }
  }, [fetchUsers]);

  const deleteUser = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const response = await apiClient.deleteUser(userId);
      if (response.success) {
        await fetchUsers(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to delete user');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      return false;
    }
  }, [fetchUsers]);

  const updateUserRole = useCallback(async (userId: string, tenantId: string, roleId: string): Promise<boolean> => {
    try {
      const response = await apiClient.updateUserRole(userId, tenantId, roleId);
      if (response.success) {
        await fetchUsers(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to update user role');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
      return false;
    }
  }, [fetchUsers]);

  const terminateUserSessions = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const response = await apiClient.terminateUserSessions(userId);
      if (response.success) {
        await fetchUsers(); // Refresh the list
        return true;
      } else {
        setError(response.error || 'Failed to terminate user sessions');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to terminate user sessions');
      return false;
    }
  }, [fetchUsers]);

  // Fetch users when filters change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    refresh,
    createUser,
    updateUser,
    deleteUser,
    updateUserRole,
    terminateUserSessions,
  };
}

export function useUser(userId: string) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getUser(userId);
      
      if (response.success) {
        setUser(response.data);
      } else {
        setError(response.error || 'Failed to fetch user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    user,
    loading,
    error,
    refresh: fetchUser,
  };
}